import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step1 from "@/components/menu-management/tea&coffe/add-tea-coffe/step1";
import Step2 from "@/components/menu-management/tea&coffe/add-tea-coffe/step2";
import Step3 from "@/components/menu-management/tea&coffe/add-tea-coffe/step3";
import { useFetchAllMenuData } from "@/hooks/use-fetch-all-menu-data";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewTeaCoffee,
  clearTeaCoffeeMessages,
  updateExistingTeaCoffee,
} from "@/redux/slice/menu-manage-slice/tea-coffee-slice";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

export const tea_coffee = 4;

function CreateTeaCoffee() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type } = useParams();
  const location = useLocation();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const item = location.state;

  const fetchOnly = useMemo(
    () => ["ingredients", "categories", "addOns", "allergens", "brands"],
    []
  );

  const { groupLoading } = useFetchAllMenuData(user.restaurant_id, tea_coffee, {
    fetchOnly,
  });

  const { createLoading, error, updateLoading } = useSelector(
    (state) => state.menuManage.teaCoffee
  );

  const form = useForm({
    resolver: zodResolver(createCoffeSchema),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: "",
      name: "",
      description: "",
      category_id: null,
      brand_id: null,
      vat: 0,
      status: "active",
      file_manager_id: undefined,
      ingredient_ids: [],
      add_on_ids: [],
      allergen_ids: [],
      variant_ids: [],
    },
  });
  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/tea-coffee");
      return;
    }
    form.reset({
      type: "update",
      item_no: item.item_no || "",
      name: item.name || "",
      description: item.description || "",
      category_id: item.category_id || null,
      brand_id: item.brand_id || null,
      vat: item.vat ?? 0,
      status: item.status || "active",
      file_manager_id: item.file_manager_id || undefined,
      ingredient_ids: item.ingredients?.map((ing) => ing.id) || [],
      add_on_ids: item.add_ons?.map((a) => a.id) || [],
      allergen_ids: item.allergens?.map((a) => a.id) || [],
      variant_ids: item.variants?.map((v) => v.id) || [],
    });

    if (item.file_manager_id && item.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
    dispatch(setItemVariants(item.variants));
  }, [type, item?.id, form]);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Tea & Coffee",
      user,
      createAction: createNewTeaCoffee,
      updateAction: updateExistingTeaCoffee,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/tea-coffee",
    });

  if (groupLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${
          type == "update" ? "Update Tea & Coffee" : "Add New Tea & Coffee"
        }`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: "Tea & Coffee", path: "/backoffice/menu/tea-coffee" },
        {
          name: `${
            type == "update" ? "Update Tea & Coffee" : "Add New Tea & Coffee"
          }`,
          path: `/backoffice/menu/tea-coffee/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {/* {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )} */}
        <Form {...form}>
          <form>
            {step === 0 && <Step1 tea_coffee={tea_coffee} form={form} />}
            {step === 1 && (
              <Step2
                item_id={entityId || item?.id}
                tea_coffee={tea_coffee}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/tea-coffee"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateTeaCoffee;

export const createCoffeSchema = z.object({
  item_no: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  category_id: z.preprocess((val) => {
    if (val === "" || val === undefined || val === null) return null;
    return Number(val);
  }, z.number().nullable().optional()),

  brand_id: z.preprocess(
    (val) => {
      if (val === undefined || val === null || val === "") return undefined;
      return Number(val);
    },
    z
      .number({
        invalid_type_error: "Brand must be a number",
      })
      .optional()
  ),
  variant_ids: z
    .array(z.number(), {
      required_error: "Please select at least one variant",
    })
    .optional(),
  ingredient_ids: z.array(z.number()).optional(),
  add_on_ids: z.array(z.number()).optional(),
  allergen_ids: z.array(z.number()).optional(),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "VAT percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "VAT percentage cannot exceed 100",
    })
    .optional(),
  file_manager_id: z
    .number({
      required_error: "Please upload an image",
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),
  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
